import { type TypeTableColumn } from '@thefarmersfront/kitchen';

import { BadgeCell, ItemListCell, TextButtonCell } from '~/components/common';

import { maskName } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { DATE_FORMAT } from '~/constants';
import { CLAIM_RETURN_STATUS_BADGE_COLOR, type TypeClaimReturnStatus } from '~/constants/claim';

import type { TypeExchangeData } from '~/types/claim.types';

/**
 * [교환 신청/조회] 페이지 > 테이블 컬럼 정의
 *
 * @note 행 선택(단일)은 No 칸 체크박스로 한다.
 */
const getClaimExchangeColumnDefs = ({
  onClickExchangeReceiptId,
  onClickExchangeOrderCode,
}: {
  onClickExchangeReceiptId: (exchangeReceiptId: string) => void;
  onClickExchangeOrderCode: (outboundOrderCode: string) => void;
}) => {
  const columnDefs: TypeTableColumn<TypeExchangeData>[] = [
    {
      id: 'no',
      header: 'No',
      cell: ({ row }) => row.index + 1,
      size: 72,
      meta: { selection: true },
    },
    {
      accessorKey: 'status',
      header: '교환상태',
      size: 100,
      cell: ({ getValue }) => {
        const value = getValue<TypeExchangeData['status']>();
        if (!value?.code) return '-';
        const code = value.code as TypeClaimReturnStatus;
        return (
          <BadgeCell
            value={code}
            colorMap={{ [code]: { label: value.name, color: CLAIM_RETURN_STATUS_BADGE_COLOR[code] } }}
            variant="light"
            status
          />
        );
      },
    },
    {
      accessorKey: 'exchangeReceiptAt',
      header: '교환접수일시',
      size: 160,
      cell: ({ getValue }) => formatDate(getValue<string>(), DATE_FORMAT.DATE_TIME) ?? '-',
    },
    {
      accessorKey: 'clientOrderCode',
      header: '원판매처 주문번호',
      size: 180,
    },
    {
      accessorKey: 'exchangeReceiptId',
      header: '교환요청번호',
      size: 180,
      cell: ({ getValue, row }) => (
        <TextButtonCell value={getValue()} onClick={() => onClickExchangeReceiptId(row.original.exchangeReceiptId)}>
          {row.original.exchangeReceiptId}
        </TextButtonCell>
      ),
    },
    {
      id: 'member.name',
      accessorFn: (data) => data.member?.name,
      header: '수령자명',
      size: 100,
      cell: ({ getValue }) => maskName(getValue<string>()) ?? '-',
    },
    {
      id: 'returnCourier.name',
      accessorFn: (data) => data.returnCourier?.name,
      header: '회수 배송사',
      size: 88,
    },
    {
      accessorKey: 'returnOrderTrackings',
      header: '회수 송장번호',
      minSize: 280,
      meta: { flex: true },
      cell: ({ getValue }) => {
        const value = getValue<TypeExchangeData['returnOrderTrackings']>();
        if (!value || value.length === 0) return '-';
        return value.length > 1 ? `${value[0].invoiceNumber} 외 ${value.length - 1}개` : value[0].invoiceNumber;
      },
    },
    {
      accessorKey: 'outboundOrderCode',
      header: '원출고요청번호',
      size: 180,
    },
    {
      id: 'returnOrderItems',
      header: '회수 상품명',
      minSize: 100,
      size: 240,
      cell: ({ row }) => <ItemListCell value={row.original.returnOrderItems} fieldName="goodsName" />,
    },
    {
      id: 'exchangeType.name',
      accessorFn: (data) => data.exchangeType?.name,
      header: '선교환여부',
      size: 100,
    },
    {
      accessorKey: 'exchangeOutboundOrderCode',
      header: '교환배송 출고요청번호',
      size: 180,
      cell: ({ getValue, row }) => (
        <TextButtonCell
          value={getValue()}
          onClick={() => onClickExchangeOrderCode(row.original.exchangeOutboundOrderCode)}
        >
          {row.original.exchangeOutboundOrderCode}
        </TextButtonCell>
      ),
    },
    {
      id: 'exchangeOrderItems',
      header: '교환 배송 상품명',
      minSize: 100,
      size: 240,
      cell: ({ row }) => <ItemListCell value={row.original.exchangeOrderItems} fieldName="goodsName" />,
    },
    {
      accessorKey: 'updatedAt',
      header: '최종 업데이트일시',
      size: 159,
      cell: ({ getValue }) => formatDate(getValue<string>(), DATE_FORMAT.DATE_TIME) ?? '-',
    },
  ];

  return { columnDefs };
};

export default getClaimExchangeColumnDefs;
